import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import styles from "./OCRPreviewForm.module.scss";

// Fields from vision prompt; name split into first_name + last_name
const SECTIONS = [
  {
    title: "Personal & contact",
    fields: [
      { key: "first_name", label: "First name", type: "text", required: true },
      { key: "last_name", label: "Last name", type: "text", required: true },
      { key: "phone", label: "Phone", type: "text" },
      { key: "telephone_number", label: "Telephone", type: "text" },
      { key: "email", label: "Email", type: "email", required: true },
      { key: "date_of_birth", label: "Date of birth", type: "date" },
      { key: "gender", label: "Gender", type: "select", options: ["Male", "Female", "Other"] },
      { key: "nic_passport", label: "NIC / Passport", type: "text" },
      { key: "address", label: "Address", type: "text" },
      { key: "country_of_residency", label: "Country of residency", type: "text" },
    ],
  },
];

const ALL_KEYS = SECTIONS.flatMap((s) => s.fields.map((f) => f.key));

const defaultForm = () =>
  ALL_KEYS.reduce((acc, key) => ({ ...acc, [key]: "" }), {});

/**
 * Build initial form state from enquiry + optional OCR data.
 * enquiry: EnquiryDetails-style (first_name, last_name, email, phone_number, nic, address, etc.).
 * ocrData: { name, first_name, last_name, phone, email, date_of_birth, ... } from vision OCR (backend splits name).
 */
export function buildInitialForm(enquiry = {}, ocrData = {}) {
  const form = defaultForm();
  // Pre-fill from enquiry
  if (enquiry.first_name != null && enquiry.first_name !== "") form.first_name = enquiry.first_name;
  if (enquiry.last_name != null && enquiry.last_name !== "") form.last_name = enquiry.last_name;
  if (enquiry.phone_number != null && enquiry.phone_number !== "") form.phone = enquiry.phone_number;
  if (enquiry.telephone_number != null && enquiry.telephone_number !== "") form.telephone_number = enquiry.telephone_number;
  if (enquiry.email != null && enquiry.email !== "") form.email = enquiry.email;
  if (enquiry.date_of_birth != null) {
    const d = enquiry.date_of_birth;
    form.date_of_birth = typeof d === "string" ? d : (d && d.toISOString ? d.toISOString().split("T")[0] : "");
  }
  if (enquiry.gender != null && enquiry.gender !== "") form.gender = enquiry.gender;
  if (enquiry.nic != null && enquiry.nic !== "") form.nic_passport = enquiry.nic;
  if (enquiry.passport != null && enquiry.passport !== "" && !form.nic_passport) form.nic_passport = enquiry.passport;
  if (enquiry.address != null && enquiry.address !== "") form.address = enquiry.address;
  if (enquiry.country_of_residency != null && enquiry.country_of_residency !== "") form.country_of_residency = enquiry.country_of_residency;
  // Overlay OCR: use first_name/last_name from API, or split name
  const ocrFirst = ocrData.first_name != null && String(ocrData.first_name).trim() ? ocrData.first_name.trim() : null;
  const ocrLast = ocrData.last_name != null && String(ocrData.last_name).trim() ? ocrData.last_name.trim() : null;
  if (ocrFirst !== null && ocrLast !== null) {
    form.first_name = ocrFirst;
    form.last_name = ocrLast;
  } else if (ocrData.name != null && String(ocrData.name).trim()) {
    const parts = String(ocrData.name).trim().split(/\s+/);
    form.first_name = parts[0] || form.first_name || "";
    form.last_name = parts.slice(1).join(" ") || form.last_name || "";
  }
  if (ocrData.phone != null && String(ocrData.phone).trim()) form.phone = ocrData.phone.trim();
  if (ocrData.telephone_number != null && String(ocrData.telephone_number).trim()) form.telephone_number = ocrData.telephone_number.trim();
  if (ocrData.email != null && String(ocrData.email).trim()) form.email = ocrData.email.trim();
  if (ocrData.date_of_birth != null && String(ocrData.date_of_birth).trim()) form.date_of_birth = ocrData.date_of_birth.trim();
  if (ocrData.gender != null && String(ocrData.gender).trim()) form.gender = ocrData.gender.trim();
  if (ocrData.nic_passport != null && String(ocrData.nic_passport).trim()) form.nic_passport = ocrData.nic_passport.trim();
  if (ocrData.address != null && String(ocrData.address).trim()) form.address = ocrData.address.trim();
  if (ocrData.country_of_residency != null && String(ocrData.country_of_residency).trim()) form.country_of_residency = ocrData.country_of_residency.trim();
  return form;
}

/**
 * Keys that OCR can fill (for "Filled by OCR" badge); matches vision prompt fields.
 */
export const OCR_FILLABLE_KEYS = new Set([
  "first_name", "last_name", "phone", "email", "date_of_birth", "nic_passport", "address", "country_of_residency", "telephone_number", "gender",
]);

/**
 * Split view: left = preview of uploaded form image, right = editable form.
 * initialData = merged enquiry + OCR (use buildInitialForm). autoFilledKeys = keys filled by OCR.
 */
export default function OCRPreviewForm({
  leadId,
  enquiryName,
  initialData = {},
  autoFilledKeys = new Set(),
  previewUrl = null,
  warnings = [],
  onSubmit,
  onCancel,
  submitLabel = "Confirm & submit",
}) {
  const [form, setForm] = useState(() => buildInitialForm(initialData.enquiry || {}, initialData.ocr || {}));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError(null);
  }, []);

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(form);
    } catch (e) {
      setError(e.message || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateForInput = (val) => {
    if (!val) return "";
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? "" : d.toISOString().split("T")[0];
  };

  return (
    <Box className={styles.root}>
      <div className={styles.previewSection}>
        <Typography className={styles.previewLabel}>Uploaded form</Typography>
        <div className={styles.previewBox}>
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="Application form preview"
              className={styles.previewImage}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No preview
            </Typography>
          )}
        </div>
      </div>
      <div className={styles.formSection}>
        <Typography className={styles.formTitle}>
          Application details
          {autoFilledKeys.size > 0 && (
            <span className={styles.autoFilledBadge}> • Auto-filled from form</span>
          )}
        </Typography>
        {warnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {warnings.join(" ")}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <div className={styles.formScroll}>
          {SECTIONS.map((section) => (
            <Box key={section.title} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                {section.title}
              </Typography>
              <div className={styles.fieldGrid}>
                {section.fields.map(({ key, label, type, required, options }) => (
                  <div key={key} className={styles.fieldRow}>
                    <label className={styles.fieldLabel}>
                      {label}
                      {required && " *"}
                      {autoFilledKeys.has(key) ? (
                        <span className={styles.autoFilledBadge}> Filled by AI</span>
                      ) : (
                        <span className={styles.autoFilledBadgeNotFound}> Not Found </span>
                      )}
                    </label>
                    {type === "select" ? (
                      <FormControl size="small" fullWidth variant="outlined">
                        <Select
                          name={key}
                          value={form[key] || ""}
                          onChange={(e) => handleChange(key, e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">Select</MenuItem>
                          {(options || []).map((opt) => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        className={styles.fieldInput}
                        size="small"
                        fullWidth
                        type={type === "date" ? "date" : type === "email" ? "email" : "text"}
                        name={key}
                        value={type === "date" ? formatDateForInput(form[key]) : (form[key] ?? "")}
                        onChange={(e) => handleChange(key, e.target.value)}
                        placeholder={label}
                        variant="outlined"
                      />
                    )}
                  </div>
                ))}
              </div>
            </Box>
          ))}
        </div>
        <div className={styles.actions}>
          {onCancel && (
            <Button variant="outlined" onClick={onCancel} disabled={submitting}>
              Back
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Saving…" : submitLabel}
          </Button>
        </div>
      </div>
    </Box>
  );
}
